import { Expense } from '../types';
import { roundMoney } from './formatMoney';

export type CurrencyOption = {
  code: string;
  name: string;
  symbol: string;
};

export const CURRENCIES: CurrencyOption[] = [
  { code: 'ILS', name: 'שקל חדש', symbol: '₪' },
  { code: 'USD', name: 'דולר אמריקאי', symbol: '$' },
  { code: 'EUR', name: 'אירו', symbol: '€' },
  { code: 'GBP', name: 'לירה שטרלינג', symbol: '£' },
  { code: 'JPY', name: 'ין יפני', symbol: '¥' },
  { code: 'CNY', name: 'יואן סיני', symbol: '¥' },
  { code: 'CHF', name: 'פרנק שוויצרי', symbol: 'CHF' },
];

export const FX_RATE_OVERRIDE_HINT = 'השער שונה בעריכה האחרונה';

export function isShekel(code: string | undefined | null): boolean {
  const text = String(code || '').trim().toUpperCase();
  return text === '' || text === 'ILS' || text === 'NIS' || text === '₪';
}

export function currencyChoices(current: string | undefined | null): CurrencyOption[] {
  const code = String(current || 'ILS').trim().toUpperCase() || 'ILS';
  if (CURRENCIES.some((item) => item.code === code)) return CURRENCIES;
  return [...CURRENCIES, { code, name: code, symbol: code }];
}

export function currencyByCode(code: string | undefined | null): CurrencyOption {
  const normalized = String(code || 'ILS').trim().toUpperCase() || 'ILS';
  return (
    CURRENCIES.find((item) => item.code === normalized) || {
      code: normalized,
      name: normalized,
      symbol: normalized,
    }
  );
}

export function formatRate(rate: number | undefined | null): string {
  const value = Number(rate);
  if (!Number.isFinite(value)) return '';
  return String(Math.round(value * 10000) / 10000);
}

export function formatForeignAmount(symbol: string, amount: number): string {
  if (!Number.isFinite(amount)) return '';
  const rounded = Math.round(amount * 100) / 100;
  const text = Number.isInteger(rounded)
    ? String(rounded)
    : rounded.toFixed(2).replace(/\.?0+$/, '');
  return `${symbol}${text}`;
}

export function optionalNumber(value: unknown): number | undefined {
  if (value === undefined || value === null || value === '') return undefined;
  const number = Number(value);
  return Number.isFinite(number) ? number : undefined;
}

export function shekelsFromOriginal(net: number, vat: number, total: number, rate: number) {
  return {
    amount: roundMoney(net * rate),
    vatAmount: roundMoney(vat * rate),
    totalAmount: roundMoney(total * rate),
  };
}

export function clearForeign(expense: Expense): Expense {
  return {
    ...expense,
    currency: 'ILS',
    originalNetAmount: undefined,
    originalVatAmount: undefined,
    originalTotalAmount: undefined,
    fxRate: undefined,
    fxRateDate: '',
    fxRateOverridden: false,
  };
}

export function applyOfficialRate(
  expense: Expense,
  currency: string,
  rate: number,
  rateDate: string
): Expense {
  const next: Expense = {
    ...expense,
    currency,
    fxRate: rate,
    fxRateDate: rateDate,
    fxRateOverridden: false,
  };
  const originalTotal = Number(expense.originalTotalAmount);
  if (Number.isFinite(originalTotal) && originalTotal !== 0) {
    const net = Number(expense.originalNetAmount ?? originalTotal);
    const vat = Number(expense.originalVatAmount ?? 0);
    return { ...next, ...shekelsFromOriginal(net, vat, originalTotal, rate) };
  }
  const shekelTotal = Number(expense.totalAmount);
  if (Number.isFinite(shekelTotal) && shekelTotal !== 0) {
    return {
      ...next,
      originalTotalAmount: shekelTotal / rate,
      originalNetAmount: Number(expense.amount || 0) / rate,
      originalVatAmount: Number(expense.vatAmount || 0) / rate,
    };
  }
  return next;
}

export function applyOriginalTotal(expense: Expense, originalTotal: number): Expense {
  const previous = Number(expense.originalTotalAmount || 0);
  let originalNet = Number(expense.originalNetAmount ?? 0);
  let originalVat = Number(expense.originalVatAmount ?? 0);
  if (previous) {
    const scale = originalTotal / previous;
    originalNet *= scale;
    originalVat *= scale;
  } else {
    originalNet = originalTotal;
    originalVat = 0;
  }
  const rate = Number(expense.fxRate || 0);
  const shekel = rate ? shekelsFromOriginal(originalNet, originalVat, originalTotal, rate) : {};
  return {
    ...expense,
    originalTotalAmount: originalTotal,
    originalNetAmount: originalNet,
    originalVatAmount: originalVat,
    ...shekel,
  };
}

export function withShekelOverride(
  expense: Expense,
  amounts: { amount: number; vatAmount: number; totalAmount?: number }
): Expense {
  const original = Number(expense.originalTotalAmount || 0);
  const total = amounts.totalAmount;
  if (isShekel(expense.currency) || !original || !total) {
    return { ...expense, ...amounts };
  }
  return {
    ...expense,
    ...amounts,
    fxRate: total / original,
    fxRateOverridden: true,
    fxRateDate: '',
  };
}
